/* eslint-disable max-lines */
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { TypographyH2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ArrowLeft, Shuffle } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { WorkHomeSuggestionAnalyticsFeatures } from '../../contracts/work-home-composer-analytics.types';
import type { WorkMode } from '../../contracts/work-mode';
import { researchPreviewTermsMessages } from '../../data/projects/researchPreviewTerms.messages';
import { useIsWorkHomeMarkEnabled } from '../../flags';
import { mintWorkHomeShuffleSeed } from './components/work-home-ranking-policy';
import {
  EMPTY_WORK_HOME_RENDER,
  recordWorkHomePick,
  recordWorkHomeRender,
  type WorkHomeRenderRecord,
} from './components/work-home-suggestion-analytics';
import { WorkHomeMark } from './components/WorkHomeMark';
import { showsWorkHomeExtras } from './WorkHomeRoutineDock';
import {
  getWorkHomeSuggestionSets,
  nextWorkHomeTraySeed,
  pickWorkHomeTrayIds,
} from './workHomeSuggestions';

/**
 * WHY THE APP-SIDE CHILDREN ARE SLOTS.
 *
 * This chrome renders five surfaces it does not own, and three of them cannot
 * follow it here: the assistant-text block reaches `@/components/chat`, the
 * desktop nurture banner reads jotai atoms, a feature flag, `isTauri` and the
 * routine-run query, and the routine rail is named in a frozen chat quarantine
 * pair. So the layout, the mode branching and the reserved-space arithmetic —
 * everything a regression would actually break — live here, and each foreign
 * child crosses as a `ReactNode` the host supplies.
 *
 * The rail is the one child that is also its own SURFACE, `WorkHomeRoutineDock`,
 * because where it sits is not a question this chrome can answer: it belongs at
 * the foot of the landing, past the slack the page owns. It took the mode gate
 * with it, and this file imports `showsWorkHomeExtras` back for the tray so the
 * two cannot drift.
 *
 * The callbacks that only ever fed one of those children moved out with it:
 * `onSelectPrompt`, `onPrefillPrompt` and `onOpenWorkflows` were props of this
 * chrome that this chrome never read. Passing a child instead of a child's
 * arguments is what keeps the DOM identical while the coupling leaves.
 */

export interface WorkHomeEmptyStateProps {
  isContextFocused: boolean;
  workMode: WorkMode;
  firstName?: string;
  showBrandMark?: boolean;
  /** Host-supplied starter copy for the focused surface. */
  assistantText: ReactNode;
  /** Host-supplied suggestion chips for the focused surface. */
  contextSuggestions: ReactNode;
  onBack: () => void;
}

/** Rendered through `ProjectChat`'s `emptyState` slot, not above the chat, so the
 *  chat keeps one element position across the landing → project URL flip. */
export const WorkHomeEmptyState = ({
  isContextFocused,
  workMode,
  firstName,
  showBrandMark = true,
  assistantText,
  contextSuggestions,
  onBack,
}: WorkHomeEmptyStateProps): JSX.Element => {
  const isWorkHomeMarkEnabled = useIsWorkHomeMarkEnabled();

  if (isContextFocused) {
    return (
      <div className="flex w-full flex-col gap-5" data-testid="work-home-focused-starter">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ms-2 w-fit text-text-secondary"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" aria-hidden />
          <FormattedMessage
            defaultMessage="Back to home"
            id="S2YAOvFqby"
            description="Work home focused state: label for the back button that clears linked context"
          />
        </Button>
        {assistantText}
        {contextSuggestions}
      </div>
    );
  }

  return workMode === 'evidence' ? (
    <div
      className="flex w-full shrink-0 flex-col items-center pb-2"
      data-testid="work-home-hero"
    >
      <TypographyH2 className="text-center font-serif text-text-primary">
        <FormattedMessage
          defaultMessage="Get clinically backed answers"
          id="bzfVbtY4ea"
          description="Evidence landing heading"
        />
      </TypographyH2>
    </div>
  ) : (
    <div
      className={cn(
        'flex w-full shrink-0 flex-col items-center',
        !isWorkHomeMarkEnabled && 'pb-2'
      )}
      data-testid="work-home-hero"
    >
      {isWorkHomeMarkEnabled && showBrandMark ? <WorkHomeMark /> : null}
      <TypographyH2
        className={
          isWorkHomeMarkEnabled ? 'sr-only' : 'text-center font-serif text-text-primary'
        }
      >
        {isWorkHomeMarkEnabled ? (
          <FormattedMessage
            defaultMessage="Home"
            id="agQs3ztlAv"
            description="Accessible Work home heading"
          />
        ) : firstName ? (
          <FormattedMessage
            defaultMessage="Welcome back, {name}"
            id="UOxi8mioge"
            values={{ name: firstName }}
          />
        ) : (
          <FormattedMessage defaultMessage="Welcome back" id="UKxoV8UIxo" />
        )}
      </TypographyH2>
      <Badge
        color="beta"
        size="sm"
        className={cn(
          'rounded-lg border border-border',
          !isWorkHomeMarkEnabled && 'mt-2'
        )}
      >
        <FormattedMessage {...researchPreviewTermsMessages.badge} />
      </Badge>
    </div>
  );
};

type WorkHomeTraySelectSuggestion = (
  prompt: string,
  suggestionId: string,
  features?: WorkHomeSuggestionAnalyticsFeatures
) => void;

type WorkHomeTraySuggestionsShown = (
  ids: readonly string[],
  features?: readonly WorkHomeSuggestionAnalyticsFeatures[]
) => void;

interface SuggestionTrayContentProps {
  disabled: boolean;
  onSelectSuggestion: WorkHomeTraySelectSuggestion;
  onSuggestionsShown?: WorkHomeTraySuggestionsShown;
  /** Pins the first Shuffle draw. Omitted draws are minted per click. */
  suggestionShuffleSeed?: number;
}

const SuggestionTrayContent = ({
  disabled,
  onSelectSuggestion,
  onSuggestionsShown,
  suggestionShuffleSeed,
}: SuggestionTrayContentProps): JSX.Element => {
  const intl = useIntl();
  const suggestionSets = getWorkHomeSuggestionSets(intl);
  const byId = new Map(
    suggestionSets.flat().map((suggestion) => [suggestion.id, suggestion])
  );
  // First paint stays the set that already leads with pre-chart and treatment lookup.
  const [drawSeed, setDrawSeed] = useState<number | null>(null);
  const suggestions =
    drawSeed === null
      ? (suggestionSets[0] ?? [])
      : pickWorkHomeTrayIds(drawSeed).flatMap((id) => {
          const suggestion = byId.get(id);
          return suggestion ? [suggestion] : [];
        });
  const shownIds = suggestions.map((suggestion) => suggestion.id).join('|');
  const shownIdsRef = useRef('');
  const trayRenderRef = useRef<WorkHomeRenderRecord>(EMPTY_WORK_HOME_RENDER);

  useEffect(() => {
    if (shownIds.length === 0) return;
    if (shownIdsRef.current === shownIds) return;
    shownIdsRef.current = shownIds;
    const ids = shownIds.split('|');
    // Tray rows are static catalogue prompts: no tasks, no focus.
    trayRenderRef.current = recordWorkHomeRender({
      items: ids.map((id) => ({ id })),
      shelf: { focus_kind: 'none', leftover_count: 0 },
    });
    onSuggestionsShown?.(ids, trayRenderRef.current.features);
    return () => {
      shownIdsRef.current = '';
    };
  }, [onSuggestionsShown, shownIds]);

  return (
    <div
      // ps-10 + the reserve's px-6 is the 64px inset the rows sit at.
      className="flex w-full flex-col ps-10"
      data-testid="work-home-suggestion-tray"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        flush="horizontal"
        className="h-8 justify-start gap-2 font-normal text-text-tertiary"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          const avoid = suggestions.map((suggestion) => suggestion.id).join('|');
          const start =
            drawSeed === null
              ? (suggestionShuffleSeed ?? mintWorkHomeShuffleSeed())
              : (drawSeed + 1) >>> 0;
          setDrawSeed(nextWorkHomeTraySeed(start, avoid));
        }}
      >
        <Shuffle className="size-4 text-text-tertiary" />
        <FormattedMessage
          defaultMessage="Shuffle"
          id="y+UDvlN36v"
          description="Work home suggestion tray: reshuffle the suggested prompt chips"
        />
      </Button>
      {suggestions.map((suggestion) => {
        const Icon = suggestion.icon;
        return (
          <Button
            key={suggestion.label}
            type="button"
            variant="ghost"
            size="sm"
            flush="horizontal"
            className="h-9 justify-start gap-2 font-normal text-text-primary"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() =>
              recordWorkHomePick(
                trayRenderRef.current,
                { id: suggestion.id },
                (_, features) =>
                  onSelectSuggestion(
                    suggestion.prompt ?? suggestion.label,
                    suggestion.id,
                    features
                  )
              )
            }
          >
            <Icon className="size-4 text-text-secondary" />
            {suggestion.label}
          </Button>
        );
      })}
    </div>
  );
};

export interface WorkHomeSuggestionTrayProps {
  showSuggestions: boolean;
  disabled: boolean;
  onSelectSuggestion: WorkHomeTraySelectSuggestion;
  onSuggestionsShown?: WorkHomeTraySuggestionsShown;
  /** Pins the first Shuffle draw. Omitted draws are minted per click. */
  suggestionShuffleSeed?: number;
}

/**
 * Auto's slot, a fixed-height member of the hero + composer group: focus only
 * changes its contents, never the group's geometry. It is the host that decides
 * the slot is Auto-only — under an Evidence composer this reserve held 160px of
 * void, and once the landing began hanging from a top inset there was no
 * geometry left for it to protect.
 */
export const WorkHomeSuggestionTray = ({
  showSuggestions,
  disabled,
  onSelectSuggestion,
  onSuggestionsShown,
  suggestionShuffleSeed,
}: WorkHomeSuggestionTrayProps): JSX.Element => (
  <div
    className="relative z-20 mx-auto h-40 w-full max-w-2xl shrink-0 overflow-hidden px-6 pt-2"
    data-testid="work-home-suggestion-reserve"
  >
    {showSuggestions ? (
      <SuggestionTrayContent
        disabled={disabled}
        onSelectSuggestion={onSelectSuggestion}
        onSuggestionsShown={onSuggestionsShown}
        suggestionShuffleSeed={suggestionShuffleSeed}
      />
    ) : null}
  </div>
);

export interface WorkHomeBelowComposerProps {
  workMode: WorkMode;
  /** Composer-only Evidence mode hides Work prompt suggestions without
   *  changing the page-level hero, nurture banner, or routine rail. */
  showSuggestions?: boolean;
  /**
   * Whether the starter slot is live, independent of the page mode. The
   * sidebar's Evidence route puts the whole page in Evidence; the composer pill
   * puts only the COMPOSER there, and both must render the same starters. Left
   * unset it follows `workMode`, which is the shape every caller had before the
   * pill needed to answer for itself.
   */
  showEvidenceStarters?: boolean;
  reserveSuggestionSpace?: boolean;
  isContextFocused: boolean;
  hasStarted: boolean;
  disabled: boolean;
  onSelectSuggestion: WorkHomeTraySelectSuggestion;
  onSuggestionsShown?: WorkHomeTraySuggestionsShown;
  /** Host-supplied Evidence-mode starters. Evidence's two-level verb
   *  suggestions live in Chat, so the Work route — itself a Chat glob — renders
   *  them and hands the node down rather than this chrome reaching for them. */
  evidenceStarters: ReactNode;
  /** Host-supplied desktop nurture banner. */
  nurtureBanner: ReactNode;
  /** Host-supplied shortcut tip — the flag and platform gates stay app-resolved. */
  shortcutTip: ReactNode;
}

/** Page-owned chrome docked beneath the composer, which must stay unaware of it. */
export const WorkHomeBelowComposer = ({
  workMode,
  showSuggestions = true,
  showEvidenceStarters,
  reserveSuggestionSpace = false,
  isContextFocused,
  hasStarted,
  disabled,
  onSelectSuggestion,
  onSuggestionsShown,
  evidenceStarters,
  nurtureBanner,
  shortcutTip,
}: WorkHomeBelowComposerProps): JSX.Element => {
  const isEvidence = workMode === 'evidence';
  const showStarters = (showEvidenceStarters ?? isEvidence) && !isContextFocused;
  const showWorkHomeExtras = showsWorkHomeExtras({
    workMode,
    showEvidenceStarters,
    isContextFocused,
    hasStarted,
  });
  const showSuggestionTray = showWorkHomeExtras && showSuggestions;
  /**
   * Evidence pays nothing for the Auto tray's anchor: `showWorkHomeExtras`
   * already excludes the starters, which is what stopped an empty anchor sitting
   * above them where a presence assertion cannot see it.
   */
  const reserveTraySpace = showWorkHomeExtras && reserveSuggestionSpace;
  const suggestionTray = (
    <SuggestionTrayContent
      disabled={disabled}
      onSelectSuggestion={onSelectSuggestion}
      onSuggestionsShown={onSuggestionsShown}
    />
  );

  return (
    <div className="shrink-0 pb-6" data-testid="work-home-below-composer">
      {/*
       * NO STACKING LEVEL HERE. This column is the content UNDER the composer,
       * and the composer's own overlays — the typing autocomplete above all,
       * which hangs out of the card on `absolute top-full` — have to cover it.
       * The card is `z-20` (it has to clear the blurred glow behind it) and the
       * sheet rides inside that context, so a `z-20` on this column tied with
       * the card and won on document order alone: the verb chips painted
       * straight through an opaque sheet. It carried one from AGE-1870, when
       * this column hosted the tray as an `absolute z-10` overlay; AGE-2002 put
       * that tray back in flow and left the level behind with nothing to lift.
       */}
      <div
        className="relative mx-auto flex w-full max-w-2xl flex-col items-center gap-3 px-6 pb-2"
        data-testid="work-home-below-composer-column"
      >
        {reserveTraySpace ? (
          // Zero-height when closed so the landing stays centred (AGE-1870).
          // When open, the tray is in document flow so Ways to work sits below
          // it instead of being painted over (AGE-2002).
          <div className="relative w-full" data-testid="work-home-suggestion-reserve">
            {showSuggestionTray ? suggestionTray : null}
          </div>
        ) : showSuggestionTray ? (
          suggestionTray
        ) : null}

        {showStarters ? evidenceStarters : nurtureBanner}

        {shortcutTip}
      </div>
    </div>
  );
};
